import { Alpha3Code } from 'i18n-iso-countries';

import { ValueOf } from '../utilities/types';
import languages, { Languages } from './languages';

const identifierTypes = {
  SOCIAL_SECURITY_NUMBER: 'socialSecurityNumber',
  NATIONAL_INSURANCE_NUMBER: 'nationalInsuranceNumber',
  NATIONAL_ID: 'nationalID',
  PASSPORT_NUMBER: 'passportNumber',
  DRIVERS_LICENSE_NUMBER: 'driversLicenseNumber',
  COORDINATION_NUMBER: 'coordinationNumber',
  OTHER: 'other',
} as const;

const identifierTypeEnLabels = {
  [identifierTypes.SOCIAL_SECURITY_NUMBER]: 'Social Security Number',
  [identifierTypes.NATIONAL_INSURANCE_NUMBER]: 'National Insurance Number',
  [identifierTypes.NATIONAL_ID]: 'National ID',
  [identifierTypes.PASSPORT_NUMBER]: 'Passport Number',
  [identifierTypes.DRIVERS_LICENSE_NUMBER]: 'Driver\'s License Number',
  [identifierTypes.COORDINATION_NUMBER]: 'Coordination Number',
  [identifierTypes.OTHER]: 'Other',
} as const;

const identifierTypeSvLabels = {
  [identifierTypes.SOCIAL_SECURITY_NUMBER]: 'Personnumer',
  [identifierTypes.NATIONAL_INSURANCE_NUMBER]: 'Nationellt försäkringsnummer',
  [identifierTypes.NATIONAL_ID]: 'Nationellt ID',
  [identifierTypes.PASSPORT_NUMBER]: 'Passnummer',
  [identifierTypes.DRIVERS_LICENSE_NUMBER]: 'Körkortsnummer',
  [identifierTypes.COORDINATION_NUMBER]: 'Samordningsnummer',
  [identifierTypes.OTHER]: 'Annat',
} as const;

const identifierTypeDeLabels = {
  [identifierTypes.SOCIAL_SECURITY_NUMBER]: 'Sozialversicherungsnummer',
  [identifierTypes.NATIONAL_INSURANCE_NUMBER]: 'Nationale Versicherungsnummer',
  [identifierTypes.NATIONAL_ID]: 'Personalausweisnummer',
  [identifierTypes.PASSPORT_NUMBER]: 'Reisepassnummer',
  [identifierTypes.DRIVERS_LICENSE_NUMBER]: 'Führerscheinnummer',
  [identifierTypes.COORDINATION_NUMBER]: 'Koordinationsnummer',
  [identifierTypes.OTHER]: 'Sonstiges',
} as const;

const identifierTypeLabels = {
  [languages.EN]: identifierTypeEnLabels,
  [languages.SV]: identifierTypeSvLabels,
  [languages.DE]: identifierTypeDeLabels,
} as const;

export const identifierTypeChoices = (language: Languages) => [
  {
    id: identifierTypes.SOCIAL_SECURITY_NUMBER,
    name: identifierTypeLabels[language][identifierTypes.SOCIAL_SECURITY_NUMBER],
  },
  {
    id: identifierTypes.NATIONAL_INSURANCE_NUMBER,
    name: identifierTypeLabels[language][identifierTypes.NATIONAL_INSURANCE_NUMBER],
  },
  {
    id: identifierTypes.NATIONAL_ID,
    name: identifierTypeLabels[language][identifierTypes.NATIONAL_ID],
  },
  {
    id: identifierTypes.PASSPORT_NUMBER,
    name: identifierTypeLabels[language][identifierTypes.PASSPORT_NUMBER],
  },
  {
    id: identifierTypes.DRIVERS_LICENSE_NUMBER,
    name: identifierTypeLabels[language][identifierTypes.DRIVERS_LICENSE_NUMBER],
  },
  {
    id: identifierTypes.COORDINATION_NUMBER,
    name: identifierTypeLabels[language][identifierTypes.COORDINATION_NUMBER],
  },
  {
    id: identifierTypes.OTHER,
    name: identifierTypeLabels[language][identifierTypes.OTHER],
  },
];

type CountryIdentifierTypeLabels = Partial<
Record<Alpha3Code, Partial<Record<IdentifierTypes, string>>>
>;

const countryIdentifierTypeLabels: CountryIdentifierTypeLabels = {
  SWE: {
    [identifierTypes.SOCIAL_SECURITY_NUMBER]: 'Social Security Number',
    [identifierTypes.COORDINATION_NUMBER]: 'Coordination Number',
  },
  FIN: {
    [identifierTypes.SOCIAL_SECURITY_NUMBER]: 'Personal Identity Code',
  },
  DNK: {
    [identifierTypes.SOCIAL_SECURITY_NUMBER]: 'CPR Number',
  },
  ZAF: {
    [identifierTypes.SOCIAL_SECURITY_NUMBER]: 'ID Number',
  },
  GBR: {
    [identifierTypes.SOCIAL_SECURITY_NUMBER]: 'National Insurance Number (NINo)',
  },
};

const defaultIdentifierTypeLabels: Partial<Record<IdentifierTypes, string>> = {
  [identifierTypes.PASSPORT_NUMBER]: 'Passport Number',
};

export const identifierTypeChoicesForCountry = (country: Alpha3Code) => {
  const typeLabels = countryIdentifierTypeLabels[country] ?? defaultIdentifierTypeLabels;
  return Object.entries(typeLabels).map(([id, label]) => ({
    id,
    name: label,
  }));
};

export type IdentifierTypes = ValueOf<typeof identifierTypes>;

export default identifierTypes;
